import { NumberPadButton } from '@/components/atoms/NumberPadButton';
import React, { useMemo } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';

export interface NumberPadProps {
    onNumberPress: (number: string | number) => void;
    onDeletePress: () => void;
    onSubmitPress?: () => void;
    disabled?: boolean;
    showDelete?: boolean;
    loading?: boolean;
}

export const NumberPad = React.memo<NumberPadProps>(function NumberPad({
    onNumberPress,
    onDeletePress,
    onSubmitPress,
    disabled = false,
    showDelete = true,
    loading = false,
}) {
    // Calculate responsive spacing based on screen height
    const screenHeight = Dimensions.get('window').height;
    // Conservative thresholds - only reduce for actually small screens
    const isVerySmallScreen = screenHeight < 650; // iPhone SE, very small Android
    const isSmallScreen = screenHeight < 750; // iPhone 12/13 mini, small Android
    
    // Responsive spacing: reduce gaps first, then sizes if needed
    const spacing = useMemo(() => {
        if (isVerySmallScreen) {
            // Very small screens: aggressive reduction
            return {
                containerPadding: 8,
                rowPaddingVertical: 1,
                rowMinHeight: 48,
                buttonMargin: 3,
                buttonSize: 50,
            };
        } else if (isSmallScreen) {
            // Small screens: moderate reduction
            return {
                containerPadding: 12,
                rowPaddingVertical: 2,
                rowMinHeight: 56,
                buttonMargin: 4,
                buttonSize: 56,
            };
        } else {
            // Normal/large screens: keep original large sizes
            return {
                containerPadding: 24,
                rowPaddingVertical: 4,
                rowMinHeight: 72,
                buttonMargin: 8,
                buttonSize: 64,
            };
        }
    }, [isSmallScreen, isVerySmallScreen]);

    const numbers = [
        [1, 2, 3],
        [4, 5, 6],
        [7, 8, 9],
        [showDelete ? 'delete' : 'empty', 0, 'submit'],
    ];

    return (
        <View style={[styles.container, { paddingHorizontal: spacing.containerPadding, direction: 'ltr' }]}>
            {numbers.map((row, rowIndex) => (
                <View
                    key={rowIndex}
                    style={[
                        styles.row,
                        {
                            paddingVertical: spacing.rowPaddingVertical,
                            minHeight: spacing.rowMinHeight,
                        },
                    ]}
                >
                    {row.map((item, colIndex) => {
                        if (item === 'empty') {
                            return (
                                <NumberPadButton
                                    key={`${rowIndex}-${colIndex}`}
                                    number=""
                                    onPress={() => { }}
                                    variant="empty"
                                />
                            );
                        }

                        if (item === 'delete') {
                            return (
                                <View key={`${rowIndex}-${colIndex}`} style={styles.cell}>
                                    <NumberPadButton
                                        number=""
                                        onPress={onDeletePress}
                                        variant="delete"
                                        disabled={disabled || loading}
                                        size={spacing.buttonSize}
                                        margin={spacing.buttonMargin}
                                    />
                                </View>
                            );
                        }

                        if (item === 'submit') {
                            return (
                                <View key={`${rowIndex}-${colIndex}`} style={styles.cell}>
                                    <NumberPadButton
                                        number=""
                                        onPress={() => onSubmitPress && onSubmitPress()}
                                        variant="submit"
                                        disabled={disabled || loading}
                                        loading={loading}
                                        size={spacing.buttonSize}
                                        margin={spacing.buttonMargin}
                                    />
                                </View>
                            );
                        }

                        return (
                            <View key={`${rowIndex}-${colIndex}`} style={styles.cell}>
                                <NumberPadButton
                                    number={item}
                                    onPress={onNumberPress}
                                    disabled={disabled || loading}
                                    size={spacing.buttonSize}
                                    margin={spacing.buttonMargin}
                                />
                            </View>
                        );
                    })}
                </View>
            ))}
        </View>
    );
});

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
    },
    cell: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
});

export default NumberPad;

